/**
 * Wheel-to-zoom on a plot.
 *
 * A hook rather than an `onWheel` prop because React registers wheel handlers
 * passively, and `preventDefault` — without which the page scrolls out from
 * under the chart you are zooming — only works on a listener attached by hand.
 *
 * It returns a **callback ref**, not a `RefObject`, and that is the whole
 * subtlety: every plot here is mounted inside a `ParentSize`, which renders
 * nothing until it has measured its container. An effect reading `ref.current`
 * on mount therefore sees `null`, has nothing to attach to, and — with no
 * dependency that ever changes — never runs again. A callback ref fires when
 * the node actually appears, which is one render later.
 *
 * Every plot that reads the shared domain uses this one, so the gesture means
 * the same thing on the charts page and on the prose pages.
 */

import { useEffect, useState } from 'react';

/** One notch of the wheel. */
export const WHEEL_ZOOM_STEP = 1.18;

export function useWheelZoom(
  zoomBy: (factor: number) => void,
): (node: SVGSVGElement | null) => void {
  const [node, setNode] = useState<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!node) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomBy(event.deltaY > 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [node, zoomBy]);

  // `setNode` is stable, so this ref callback never detaches on a re-render.
  return setNode;
}
